import React, { useState, useEffect } from 'react';
import { Gift, X, Eye, Heart, Video, Calendar, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { API_URL } from '../lib/api-client';
import { logger } from '../utils/logger';

interface Reward {
  _id: string;
  rewardType: string;
  amount: number;
  reason: string;
  createdAt: string;
}

export function RewardsPopup() {
  const [pendingRewards, setPendingRewards] = useState<Reward[]>([]);
  const [currentReward, setCurrentReward] = useState<Reward | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const fetchPendingRewards = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      logger.log('🎁 Checking for pending rewards...');

      const response = await fetch(`${API_URL}/rewards/pending`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        logger.log('📥 Pending rewards:', data);

        if (data.success && data.data?.rewards?.length > 0) {
          const rewards = data.data.rewards;
          
          // Get viewed reward IDs from localStorage
          const viewedRewardIds = JSON.parse(localStorage.getItem('viewedRewardIds') || '[]');
          logger.log('👁️ Already viewed reward IDs:', viewedRewardIds);

          // Find first reward that hasn't been viewed
          const unviewedReward = rewards.find((reward: Reward) => 
            !viewedRewardIds.includes(reward._id)
          );

          if (unviewedReward) {
            logger.log('🎉 Found unviewed reward:', unviewedReward);
            setCurrentReward(unviewedReward);
            setIsVisible(true);
          } else {
            logger.log('✅ All rewards already viewed');
          }
        } else {
          logger.log('📭 No pending rewards');
        }
      }
    } catch (error) {
      logger.error('❌ Failed to fetch pending rewards:', error);
    }
  };

  const markAsViewed = (rewardId: string) => {
    const viewedRewardIds = JSON.parse(localStorage.getItem('viewedRewardIds') || '[]');
    if (!viewedRewardIds.includes(rewardId)) {
      viewedRewardIds.push(rewardId);
      localStorage.setItem('viewedRewardIds', JSON.stringify(viewedRewardIds));
      logger.log('✅ Marked reward as viewed:', rewardId);
    }
  };

  const handleClose = async () => {
    if (currentReward) {
      // Mark as viewed
      markAsViewed(currentReward._id);

      // Mark as viewed on backend
      try {
        const token = localStorage.getItem('token');
        await fetch(`${API_URL}/rewards/${currentReward._id}/mark-viewed`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (error) {
        logger.error('Failed to mark reward as viewed on backend:', error);
      }
    }

    setIsVisible(false);
    setCurrentReward(null);

    // Check for more rewards after a delay
    setTimeout(() => {
      fetchPendingRewards();
    }, 500);
  };

  // Check for pending rewards on mount and every 30 seconds
  useEffect(() => {
    const checkRewards = () => {
      fetchPendingRewards();
    };

    // Initial check after 2 seconds
    const initialTimeout = setTimeout(checkRewards, 2000);

    // Periodic check every 30 seconds
    const interval = setInterval(checkRewards, 30000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, []);

  const getRewardIcon = (type: string) => {
    switch (type) {
      case 'contactViews':
        return <Eye className="size-12 text-blue-600" />;
      case 'interests':
        return <Heart className="size-12 text-pink-600" />;
      case 'videoCallCredits':
        return <Video className="size-12 text-purple-600" />;
      case 'planValidityDays':
        return <Calendar className="size-12 text-green-600" />;
      case 'superInterests':
        return <Sparkles className="size-12 text-amber-600" />;
      default:
        return <Gift className="size-12 text-purple-600" />;
    }
  };

  const getRewardTitle = (type: string, amount: number) => {
    switch (type) {
      case 'contactViews':
        return `+${amount} Contact View Credits!`;
      case 'interests':
        return `+${amount} Interest Credits!`;
      case 'videoCallCredits':
        return `+${amount} Video Call Credits!`;
      case 'planValidityDays':
        return `+${amount} Days Plan Extension!`;
      case 'superInterests':
        return `+${amount} Super Interest Credits!`;
      default:
        return 'You Got a Reward!';
    }
  };

  const getRewardDescription = (type: string) => {
    switch (type) {
      case 'contactViews':
        return 'View more contact details of profiles you like';
      case 'interests':
        return 'Send more interests to potential matches';
      case 'videoCallCredits':
        return 'Connect face-to-face with video calls';
      case 'planValidityDays':
        return 'Your plan validity has been extended';
      case 'superInterests':
        return 'Stand out with super interests';
      default:
        return 'Enjoy your new benefits!';
    }
  };

  const getRewardColor = (type: string) => {
    switch (type) {
      case 'contactViews':
        return 'from-blue-500 to-blue-600';
      case 'interests':
        return 'from-pink-500 to-pink-600';
      case 'videoCallCredits':
        return 'from-purple-500 to-purple-600';
      case 'planValidityDays':
        return 'from-green-500 to-green-600';
      case 'superInterests':
        return 'from-amber-500 to-amber-600';
      default:
        return 'from-purple-500 to-pink-600';
    }
  };

  if (!isVisible || !currentReward) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <Card className="max-w-md w-full bg-white rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Header with gradient background */}
        <div className={`bg-gradient-to-r ${getRewardColor(currentReward.rewardType)} p-6 text-white relative overflow-hidden`}>
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-white hover:bg-white/20"
            onClick={handleClose}
          >
            <X className="size-4" />
          </Button>
          
          <div className="flex flex-col items-center text-center pt-4">
            <div className="mb-4 animate-bounce">
              {getRewardIcon(currentReward.rewardType)}
            </div>
            <h2 className="text-2xl font-bold mb-2">
              🎉 Congratulations! 🎉
            </h2>
            <p className="text-white/90 text-sm">
              You've received a special reward!
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="text-center">
            <Badge className={`bg-gradient-to-r ${getRewardColor(currentReward.rewardType)} text-white text-lg px-4 py-2 mb-3`}>
              {getRewardTitle(currentReward.rewardType, currentReward.amount)}
            </Badge>
            <p className="text-gray-600 mb-4">
              {getRewardDescription(currentReward.rewardType)}
            </p>
          </div>

          {/* Reason */}
          {currentReward.reason && (
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
              <p className="text-sm font-medium text-gray-900 mb-1">Reward Reason:</p>
              <p className="text-sm text-gray-700">{currentReward.reason}</p>
            </div>
          )}

          {/* Action Button */}
          <Button
            onClick={handleClose}
            className={`w-full bg-gradient-to-r ${getRewardColor(currentReward.rewardType)} hover:opacity-90 text-white`}
          >
            <Gift className="size-4 mr-2" />
            Claim Reward
          </Button>
        </div>
      </Card>
    </div>
  );
}